import storageSession from 'redux-persist/lib/storage/session';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import * as apis from '@/apis/auth';
import * as apisAccount from '@/apis/account';
import * as actions from '@/actions/auth';
import * as promoConfigActions from '@/actions/promoConfigs'

import {
  removeApigeeToken,
  removexAuthToken,
  setApigeeToken,
  setAuthorizationToken,
  setxAuthToken,
  setxPageKeyToken,
  setDefaultHeader,
  setXClientHeader,
  setToken
} from '@/helpers/axios';
import {
  AUTH_REGISTER_DEVICE_REQUEST,
  AUTH_REQUEST_OTP_REQUEST,
  AUTH_VERIFY_OTP_REQUEST,
  AUTH_LOGOUT_REQUEST,
  CHECK_BLACK_LIST,
  GET_MST_KEY,
  GET_GLOBAL_CONFIGS_REQUEST,
  GET_PDPA_KEY_REQUEST,
  USER_GROUP_POLICIES_REQUEST,
  REGISTER_UDF_MYEMPOWER_REQUEST,
  REGISTER_UDF_EMPOWER_REQUEST,
  REGISTER_UDF_PDPA_REQUEST,
  REGISTER_DEVICE_MFA_REQUEST,
  GET_PDPA_ORDER_REQUEST,
  GET_EMPOWER_USER_DETAILS_REQUEST,
  UPDATE_EMPOWER_USER_DETAILS_REQUEST,
  GET_WAIVER_LOGIN_DETAILS_REQUEST
} from '../../actions/auth/actionTypes';
import { AuthRequestOtp, AuthVerifyOtp, checkBlacklisted, IConfigList, IMSTKey } from '@/actions/auth/models/types';
import TokenService from '@/helpers/tokenService';
import { checkUDFTokenExpired, getConfigurationValue, isEmpowerFlow, navLinks } from '@/helpers/common';
import paths from '@/helpers/navigation/paths.json';
import { saveOriginalUrlBeforeLogin } from '@geit/core'
import { isArray } from 'lodash';

export const getAuth = (state) => {
  return state.auth;
};

function getWindowRedirect(alert = true) {
  saveOriginalUrlBeforeLogin(true);
  localStorage.removeItem('_udfChannelToken')
  alert && window.alert("You are not authorized to access this site. Please contact the system administrator.");
  window.location.replace(navLinks.myEmpowersite);
}

function getWindowBlackListedRedirect(alertMsg) {
  localStorage.removeItem('_udfChannelToken')
  window.alert(`${alertMsg}`);
  window.location.replace(navLinks.myEmpowersite);
}

function* registerDevice() {
  try {
    if (localStorage.getItem('myudf_token')) {
      localStorage.removeItem('udf_token');
      localStorage.removeItem('channelTokenPdpa');
      localStorage.removeItem('pdpaToken');
      localStorage.removeItem('customer_flow')
      yield put(actions.registerUdfMyempower.request());
    } else if (localStorage.getItem('udf_token')) {
      localStorage.removeItem('myudf_token');
      localStorage.removeItem('channelTokenPdpa');
      localStorage.removeItem('pdpaToken');
      localStorage.removeItem('customer_flow')
      yield put(actions.registerUdfEmpower.request());
    } else {
      localStorage.removeItem('myudf_token');
      localStorage.removeItem('udf_token');
      localStorage.removeItem('_udfChannelToken');
      localStorage.removeItem('customer_flow')
      if (localStorage.getItem('pdpaToken')) {
        yield put(actions.registerUdfPdpa.request());
      }
      yield put(actions.getUdfToken.success());
    }
    const siteUrl = process.env.GATSBY_UDF_SIT_URL || '';
    const substr = 'myempower';
    if (siteUrl.includes(substr) && !localStorage.getItem('myudf_token')) {
      yield put(actions.getUdfToken.failure());
      getWindowRedirect(false);
    }
  } catch (error: any) {
    yield put(actions.getUdfToken.failure());
    getWindowRedirect();
  }
}
// MFA LOGIN
function* registerDeviceMfa() {
  try {
    const result = yield call(apis.registerDevice);
    const { id, key } = result.data;
    yield call(setDefaultHeader, id, key);
    yield put(
      actions.registerDeviceMfa.success({
        clientCode: id,
        privateKey: key
      })
    );
  } catch (error: any) {
    yield put(actions.registerDeviceMfa.failure(error));
  }
}

function* fetchLoginOTP({ email }: AuthRequestOtp) {
  try {
    const userEmail = email.toLowerCase();
    const { key } = yield select(getAuth);
    const checkPdpa = (key !== undefined && key.length > 0) || false;
    if (!checkPdpa) {
      yield call(apis.customerValidate, userEmail);
    }
    const result = yield call(apis.requestOTP, email);
    yield put(
      actions.getOTP.success({
        email: userEmail,
        challengeToken: result.data.challengeToken,
        retryAfter: result.data.retryAfter
      })
    );
  } catch (error) {
    yield put(actions.getOTP.failure(error));
  }
}

function* checkBlackList({ originalOpt, brn, checkCall }: checkBlacklisted) {
  try {
    const result = yield call(apis.checkBrnBlacklisted, brn);
    yield put(actions.checkBlacklistedbrn.success(result.data));
    if (!result.data.blackListed) {
      if (!checkCall) {
        if (Object.keys(result.headers).includes('x-page-key')) {
          yield put(actions.getCheckBlacklist.success(result.headers['x-page-key']))
          const { check } = yield select(getAuth);
          yield call(setxPageKeyToken, check);
        }
        yield put(actions.getUserGroupPolicies.request())
        yield put(promoConfigActions.checkIfPromoConfigured.request({ brn: brn.brn }));
      }
      yield put(actions.selectAccount(originalOpt));
    } else if (result.data.blackListed && isEmpowerFlow()) {
      const alertMsg = 'This account is currently disabled for eshop transactions. Please contact customer support for assistance.';
      getWindowBlackListedRedirect(alertMsg);
    }
  } catch (error: any) {
    yield put(actions.checkBlacklistedbrn.failure(error));
    if (isEmpowerFlow()) {
      const alertMsg = `${error?.response?.data?.message || 'Something went wrong with our system, please contact our customer support or try again later.'}`
      getWindowBlackListedRedirect(alertMsg);
      yield put(actions.getEmpowerUserDetails.failure(error));
    }
  }
}

function* requestVerifyOTP({ challengeToken, otp }: AuthVerifyOtp) {
  try {
    const verification = yield call(apis.verifyOTP, challengeToken, otp);
    const { userInfo: { pdpaEmail, brn }, key } = yield select(getAuth);
    let accountList;
    if (key !== undefined && key.length > 0) {
      const pdpaChannelToken = JSON.parse(localStorage.getItem('channelTokenPdpa') || '{}');
      yield call(setApigeeToken, pdpaChannelToken.access_token);
      yield call(setxAuthToken, verification.data.accessToken);
      const { pdpaType } = yield select(getAuth);
      accountList = yield call(apis.pdpaAuthenticate, key, pdpaType);
      yield call(setXClientHeader, 'EMS-ESHOP-PDPA');
      yield call(removexAuthToken);
      yield call(setxAuthToken, accountList.data.accessToken);
      if (pdpaType !== 'esim') {
        yield put(actions.getPDPAOrderDetails.request(key));
      }
    } else {
      accountList = yield call(apis.getAccountList);
      yield call(setToken, verification.data.accessToken);
    }

    const result = { ...verification.data, ...accountList.data };
    window.localStorage.setItem('pdpaToken', JSON.stringify(accountList.data));
    yield put(actions.verifyOTP.success(result));
    if (key === '' || (key === undefined && key.length === 0)) {
      yield put(actions.checkBlacklistedbrn.request({ brn: brn }, pdpaEmail));
    }
  } catch (error) {
    yield put(actions.verifyOTP.failure(error));
  }
}

function* getConfiguredMstKeyValuesByBrn({ brn }: IMSTKey) {
  try {
    const result = yield call(apis.getConfiguredMstKeyValuesByBrn, brn);
    const configurationList = {};
    if (result.data && isArray(result.data)) {
      result.data.forEach((item: IConfigList) => {
        configurationList[item.keyName] = getConfigurationValue(item);
      });
    }
    yield put(actions.getConfiguredMstKeyValuesByBrn.success(configurationList));
  } catch (error) {
    yield put(actions.getConfiguredMstKeyValuesByBrn.failure(error));
  }
}

function* logout() {
  try {
    yield call(removeApigeeToken);
    yield call(removexAuthToken);
    storageSession.removeItem('persist:auth');
    TokenService.removeLocalTokenData();
    yield put(actions.logout.success());
    setTimeout(() => {
      window.location.replace(navLinks.gatsbyLogoutUrl);
    }, 500);
  } catch (error) {
    yield put(actions.logout.failure(error));
  }
}

function* getGlobalConfigValues() {
  try {
    const result = yield call(apis.getGlobalConfigs);
    const configsList = {};
    result.data.map((item) => {
      configsList[item.keyCode] = item.keyValue;
    });
    yield put(actions.getGlobalConfigs.success(configsList));
  } catch (error) {
    yield put(actions.getGlobalConfigs.failure(error));
  }
}

function* getPDPALOGINDetails({ key, pdpaType }: any) {
  try {
    const result = yield call(apis.pdpaValidate, key, false, pdpaType);
    const pdpaResult = { encLink: { key: key } };
    yield put(actions.getPDPAValidation.success({ ...result, ...pdpaResult }));
    window.localStorage.setItem('channelTokenPdpa', JSON.stringify(result.data));
    yield call(setApigeeToken, result?.data?.access_token);
    yield put(actions.registerDeviceMfa.request());
  } catch (error) {
    yield put(actions.getPDPAValidation.failure(error));
  }
}

function* getPDPAOrderDetails(key) {
  try {
    const result = yield call(apis.pdpaOrderDetails, key.key);
    yield put(actions.getPDPAOrderDetails.success(result));
  } catch (error) {
    yield put(actions.getPDPAOrderDetails.failure(error));
  }
}

function* getUserGroupPolicy() {
  try {
    let result;
    if (localStorage.getItem('udf_token')) {
      result = yield call(apis.getEmpowerUserGroupPolicies);
    } else {
      const { isStaffLogin } = yield select(getAuth);
      if (isStaffLogin) {
        result = yield call(apis.getStaffUserGroupPolicies);
      } else {
        result = yield call(apis.getUserGroupPolicies);
      }
    }
    yield put(actions.getUserGroupPolicies.success(result.data));
  } catch (error) {
    yield put(actions.getUserGroupPolicies.failure(error));
  }
}

function* registerUdfMyempower() {
  // UDF STANDALONE
  // let demoResult = yield call(apis.fetchUDFToken);
  // demoResult.data['tokenB'] = demoResult.data.accessToken;
  // window.localStorage.setItem('myudf_token', JSON.stringify(demoResult.data))
  try {
    const { isStaffLogin } = yield select(getAuth);
    yield put(actions.getUdfToken.request());
    let access_token = TokenService.getLocalAccessToken();
    if (checkUDFTokenExpired()) {
      const refreshData = yield call(apis.getRefreshToken);
      TokenService.setLocalTokenData(refreshData.data);
      access_token = refreshData.data.tokenB;
    }
    yield call(setAuthorizationToken, access_token);
    const tokenResponse = yield call(apis.fetchChannelToken);
    yield call(setApigeeToken, tokenResponse.data.access_token);
    TokenService.setUDFChannelToken(tokenResponse.data.access_token);
    yield call(setxAuthToken, access_token);
    if (typeof window !== 'undefined' && window.location.pathname.includes(paths.STAFF_LOGIN.url) || isStaffLogin) {
      setXClientHeader('EMS-ESHOP-STAFF')
    }
    const res = yield call(apis.getAccountList);
    yield put(actions.setUDFDetails(res.data));
    yield put(actions.getUserGroupPolicies.request())
    yield put(actions.getUdfToken.success());
    if (isStaffLogin) {
      setXClientHeader('EMS-ESHOP-STAFF')
    }
  } catch (error) {
    yield put(actions.getUdfToken.failure());
    getWindowRedirect();
  }
}

function* registerUdfEmpower() {
  // let demoResult = yield call(apis.fetchUDFToken);
  // demoResult.data['tokenB'] = demoResult.data.accessToken;
  // window.localStorage.setItem('udf_token', JSON.stringify(demoResult.data))
  try {
    const { isLoggedIn } = yield select(getAuth);
    yield put(actions.getUdfToken.request());
    let access_token = TokenService.getLocalAccessToken();
    yield call(setXClientHeader, 'EMS-ESHOP-EXT');
    if (checkUDFTokenExpired()) {
      const refreshData = yield call(apis.getRefreshToken);
      TokenService.setEmpowerTokenData(refreshData.data);
      access_token = refreshData.data.accessToken;
    }
    yield call(setAuthorizationToken, access_token);
    const tokenResponse = yield call(apis.fetchChannelToken);
    yield call(setApigeeToken, tokenResponse.data.access_token);
    TokenService.setUDFChannelToken(tokenResponse.data.access_token);
    // if (TokenService.getUDFChannelToken()?.length < 1) {
    //   yield call(setAuthorizationToken, access_token);
    //   const tokenResponse = yield call(apis.fetchChannelToken);
    //   yield call(setApigeeToken, tokenResponse.data.access_token);
    //   TokenService.setUDFChannelToken(tokenResponse.data.access_token);
    // } else {
    //   yield call(setAuthorizationToken, access_token);
    //   yield call(setApigeeToken, TokenService.getUDFChannelToken());
    // }
    // const { currentAccount: { brn = '' } } = yield select(getAuth);
    yield call(setxAuthToken, access_token);
    const res = yield call(apis.getAccountList);
    yield put(actions.setUDFDetails(res.data));
    if (isLoggedIn) {
      yield put(actions.updateEmpowerUserDetails.request())
    }
    yield put(actions.getUdfToken.success());
  } catch (error) {
    yield put(actions.getUdfToken.failure());
    getWindowRedirect();
  }
}

function* registerUdfPdpa() {
  try {
    yield put(actions.getUdfToken.request());
    const {
      userInfo: { token },
      key
    } = yield select(getAuth);
    if (key !== undefined && key.length > 0) {
      yield put(actions.registerDeviceMfa.request());
      const newDate = new Date();
      const pdpaToken = JSON.parse(localStorage.getItem('pdpaToken') || '{}');
      let apigee_token = token;
      let access_token = pdpaToken.accessToken;
      if (pdpaToken.expiryAt !== 0 && pdpaToken.expiryAt < newDate.getTime()) {
        const result = yield call(apis.pdpaValidate, key);
        const pdpaResult = { encLink: { key: key } };
        window.localStorage.setItem('channelTokenPdpa', JSON.stringify(result.data));
        yield put(actions.getPDPAValidation.success({ ...result, ...pdpaResult }));
        yield call(setApigeeToken, result?.data.access_token);
        yield call(setxAuthToken, pdpaToken.refreshToken);
        apigee_token = result.data.access_token;
        yield call(setxAuthToken, pdpaToken.refreshToken);
        const refreshData = yield call(apis.pdpaRefreshToken, key);
        localStorage.setItem('pdpaToken', JSON.stringify(refreshData.data));
        access_token = refreshData.data.accessToken;
      }
      yield call(setApigeeToken, apigee_token);
      yield call(setxAuthToken, access_token);
    }
    yield put(actions.getUdfToken.success());
  } catch (error) {
    yield put(actions.getUdfToken.failure());
  }
}

function* getEmpowerUserDetails() {
  try {
    const result = yield call(apis.getEmpowerUserDetails);
    yield put(actions.getEmpowerUserDetails.success(result.data));
  } catch (error) {
    yield put(actions.getEmpowerUserDetails.failure(error));
  }
}

function* updateEmpowerUserDetails() {
  try {
    const { userInfo: { email }, currentAccount: { brn = '' } } = yield select(getAuth);
    const result = yield call(apis.getEmpowerUserDetails);
    const brnNumberDetail = result.data?.entitySelection?.refNumber
    if (brnNumberDetail !== brn) {
      yield put(actions.getCheckBlacklist.success(''))
      const gridOption = {
        searchTerm: brnNumberDetail,
        custType: 'BRN',
        currentPage: 1,
        pageSize: 10
      };
      const resultAcc = yield call(apisAccount.getAccountPage, { ...gridOption, email });
      if (resultAcc.data?.brnList) {
        const brn = {
          brn: brnNumberDetail,
          email: email
        };
        let selectedBrn = resultAcc.data?.brnList.filter(item => item.brn === brn.brn);
        if (selectedBrn.length === 0) {
          selectedBrn = resultAcc.data?.brnList;
        }
        yield put(actions.checkBlacklistedbrn.request(brn, { ...selectedBrn[0] }));
      //   const chkblkLst = yield put(actions.checkBlacklistedbrn.request(brn, { ...resultAcc.data.brnList[0] }));
      //   if (!chkblkLst.data.blackListed) {
      //     if (Object.keys(chkblkLst.headers).includes('x-page-key')) {
      //       yield put(actions.getCheckBlacklist.success(chkblkLst.headers['x-page-key']))
      //       const { check } = yield select(getAuth);
      //       yield call(setxPageKeyToken, check);
      //     }
        
      // } 
      }
    } else {
      yield put(actions.getUserGroupPolicies.request())
    }
    yield put(actions.getEmpowerUserDetails.success(result.data));
  } catch (error) {
    yield put(actions.getEmpowerUserDetails.failure(error));
  }
}

function* getWaiverLoginDetails(reqData) {
  try {
    const result = yield call(apis.pdpaValidate, reqData.key, true);
    const storeDetail = {
      encLink: reqData.key,
      channelToken: result.data.access_token,
      client_id: result.data.client_id,
      email: result.data.email
    };
    yield put(actions.getWaiverLoginDetails.success(storeDetail));
    window.localStorage.setItem('_channelTokenApproval', JSON.stringify(result.data));
    yield call(setApigeeToken, result.data.access_token);
    yield put(actions.registerDeviceMfa.request());
  } catch (error) {
    yield put(actions.getWaiverLoginDetails.failure(error));
  }
}

export default function* authSagas() {
  yield takeLatest(AUTH_REGISTER_DEVICE_REQUEST, registerDevice);
  yield takeLatest(AUTH_REQUEST_OTP_REQUEST, fetchLoginOTP);
  yield takeLatest(AUTH_VERIFY_OTP_REQUEST, requestVerifyOTP);
  yield takeLatest(CHECK_BLACK_LIST, checkBlackList);
  yield takeLatest(GET_MST_KEY, getConfiguredMstKeyValuesByBrn);
  yield takeLatest(AUTH_LOGOUT_REQUEST, logout);
  yield takeLatest(GET_GLOBAL_CONFIGS_REQUEST, getGlobalConfigValues);
  yield takeLatest(GET_PDPA_KEY_REQUEST, getPDPALOGINDetails);
  yield takeLatest(GET_PDPA_ORDER_REQUEST, getPDPAOrderDetails);
  yield takeLatest(USER_GROUP_POLICIES_REQUEST, getUserGroupPolicy);
  yield takeLatest(REGISTER_UDF_MYEMPOWER_REQUEST, registerUdfMyempower);
  yield takeLatest(REGISTER_UDF_EMPOWER_REQUEST, registerUdfEmpower);
  yield takeLatest(REGISTER_UDF_PDPA_REQUEST, registerUdfPdpa);
  yield takeLatest(REGISTER_DEVICE_MFA_REQUEST, registerDeviceMfa);
  yield takeLatest(GET_EMPOWER_USER_DETAILS_REQUEST, getEmpowerUserDetails);
  yield takeLatest(UPDATE_EMPOWER_USER_DETAILS_REQUEST, updateEmpowerUserDetails);
  yield takeLatest(GET_WAIVER_LOGIN_DETAILS_REQUEST, getWaiverLoginDetails);
}